import type {
  AdminDraftResponse,
  AdminSession,
  MediaAsset,
  MediaKind,
  PublicWorkspaceResponse,
  PublishResponse,
  RestaurantWorkspace,
} from '../types/workspace';

const API_BASE = import.meta.env.VITE_FUNCTIONS_BASE_URL ?? '/api';

interface ApiErrorPayload {
  error?: string;
  message?: string;
}

const buildUrl = (path: string) => `${API_BASE}/${path}`;

const readJson = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();

  if (!contentType.includes('application/json')) {
    if (/<!doctype html>|<html/i.test(text)) {
      throw new Error(
        'The backend is not available on this preview server. Start the app with Cloudflare Wrangler or connect VITE_FUNCTIONS_BASE_URL.',
      );
    }

    throw new Error('The server returned a non-JSON response.');
  }

  const data = JSON.parse(text) as T & ApiErrorPayload;

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed');
  }

  return data as T;
};

const withAdminCredentials = (init?: RequestInit): RequestInit => ({
  ...init,
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    ...(init?.headers ?? {}),
  },
});

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Could not read file'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

export const fetchPublicWorkspace = async (): Promise<PublicWorkspaceResponse> =>
  readJson<PublicWorkspaceResponse>(await fetch(buildUrl('public-menu')));

export const fetchAdminSession = async (): Promise<AdminSession> =>
  readJson<AdminSession>(await fetch(buildUrl('admin-session'), { credentials: 'include' }));

export const loginAdmin = async (passcode: string): Promise<AdminSession> =>
  readJson<AdminSession>(
    await fetch(
      buildUrl('admin-login'),
      withAdminCredentials({
        method: 'POST',
        body: JSON.stringify({ passcode }),
      }),
    ),
  );

export const logoutAdmin = async (): Promise<void> => {
  await readJson<{ success: boolean }>(
    await fetch(buildUrl('admin-logout'), withAdminCredentials({ method: 'POST', body: '{}' })),
  );
};

export const fetchAdminDraft = async (): Promise<AdminDraftResponse> =>
  readJson<AdminDraftResponse>(await fetch(buildUrl('admin-get-draft'), { credentials: 'include' }));

export const saveAdminDraft = async (workspace: RestaurantWorkspace): Promise<AdminDraftResponse> =>
  readJson<AdminDraftResponse>(
    await fetch(
      buildUrl('admin-save-draft'),
      withAdminCredentials({
        method: 'POST',
        body: JSON.stringify({ workspace }),
      }),
    ),
  );

export const publishAdminDraft = async (): Promise<PublishResponse> =>
  readJson<PublishResponse>(
    await fetch(buildUrl('admin-publish'), withAdminCredentials({ method: 'POST', body: '{}' })),
  );

export const uploadAdminImage = async (
  file: File,
  kind: MediaKind,
  altText: string,
): Promise<MediaAsset> => {
  const base64 = await fileToDataUrl(file);

  return readJson<MediaAsset>(
    await fetch(
      buildUrl('admin-upload-image'),
      withAdminCredentials({
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type,
          base64,
          altText,
          kind,
        }),
      }),
    ),
  );
};
